import * as React from 'react';
import { Box, Paper, Typography, Button } from '@mui/material';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { useGetAllActions } from '../../hooks/use-get-all-actions';
import { useAvatar } from '../../hooks/use-get-avatar.hook';
import { CircularProgress } from '@mui/material';
import { Link } from 'react-router-dom';
import { getLogActionPath } from '../../constants/log-action-paths';
import { useDashboardData } from './DashboardProvider';
import useTranslation from '../customHooks/translations';
import { ACTION_COLOR_TOKENS } from '../../constants/action-colors';

ChartJS.register(ArcElement, Tooltip, Legend);

const ActionsTile = () => {
  const translation = useTranslation();
  const { availableActions, loading } = useGetAllActions();
  const { actionsObject } = useDashboardData();
  const {weekly_unvalidated, weekly_number_of_actions} = actionsObject;

  const sortedActions = actionsObject
    ? Object.values(actionsObject.actions_by_id).sort((a, b) => b.number - a.number)
    : [];

  const { avatar } = useAvatar();

  const existingActionIds = actionsObject && actionsObject.actions_by_id
    ? Object.values(actionsObject?.actions_by_id).map(action => action.id)
    : [];

  const remainingActions = availableActions.filter(action => !existingActionIds.includes(action.id));

  const getActionIcon = (id) => {
    const actionObject = avatar.actions.find((action) => action.id === id);
    return actionObject?.icon ? actionObject.icon : avatar.imageUrl;
  };

  const getActionColor = (id) => {
    const match = ACTION_COLOR_TOKENS.find((c) => c.id === id);
    if (match?.color) return match.color;
    return typeof id === 'number'
      ? ACTION_COLOR_TOKENS[id % ACTION_COLOR_TOKENS.length].color
      : ACTION_COLOR_TOKENS[0].color;
  };

  const getActionLightBg = (id) => {
    const match = ACTION_COLOR_TOKENS.find((c) => c.id === id);
    if (match?.lightBg) return match.lightBg;
    return typeof id === 'number'
      ? ACTION_COLOR_TOKENS[id % ACTION_COLOR_TOKENS.length].lightBg
      : ACTION_COLOR_TOKENS[0].lightBg;
  };

  const getActionLandingURL = (id) => {
    const match = ACTION_COLOR_TOKENS.find((c) => c.id === id);
    if (match?.landingUrl) return match.landingUrl;
    return typeof id === 'number'
      ? ACTION_COLOR_TOKENS[id % ACTION_COLOR_TOKENS.length].landingUrl
      : ACTION_COLOR_TOKENS[0].landingUrl;
  };

  const actionsArray = React.useMemo(() => (
    actionsObject && actionsObject.actions_by_id
      ? Object.values(actionsObject.actions_by_id).map((action, index) => ({
          ...action,
          color: getActionColor(action.id),
          key: index,
        }))
      : []
  ), [actionsObject]);

  const data = React.useMemo(() => {
    if (loading) {
      return {
        labels: [],
        datasets: [{
          label: 'My Actions',
          data: [],
          backgroundColor: [],
          borderColor: ['#FFFFFF'],
          borderWidth: 3,
        }],
      };
    }

    return {
      labels: actionsArray.map((action) => `${action.action_name} : ${action.number} `),
      datasets: [{
        label: 'My Actions',
        data: actionsArray.map((action) => action.number),
        backgroundColor: actionsArray.map((action) => action.color),
        borderColor: ['#FFFFFF'],
        borderWidth: 3,
      }],
    };
  }, [loading, actionsArray]);

  const options = {
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        displayColors: true,
        backgroundColor: '#0F172A',
        bodyFont: { weight: 'bold', size: 14, family: 'DM Sans' },
        bodyColor: '#FFFFFF',
        cornerRadius: 8,
        padding: 10,
        callbacks: {
          label: function(context) {
            return context.label;
          },
        },
      },
    },
  };

  if (loading && actionsArray.length === 0) {
    return <CircularProgress sx={{ color: '#0D9488' }} />;
  }

  return (
    <>
    <Box sx={{
      width: '100%',
      display: {xs: 'block', md: 'flex'},
      justifyContent: 'space-between',
    }}>
      <Box>
        <Paper sx={{
          flex: '1',
          alignSelf: 'start',
          margin: '1.5em',
          borderRadius: {xs:'14px', md: '99em'},
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: '1.2em 1.5em 1.8em 1.5em',
          aspectRatio: {md:'1/1'},
          maxWidth: {md: '80px'},
          background: 'linear-gradient(135deg, #0F766E 0%, #0D9488 45%, #14B8A6 100%)',
          color: '#FFFFFF',
          border: 'none',
          boxShadow: '0 4px 16px rgba(13,148,136,0.2)',
        }}>
          <Typography variant="h2" sx={{fontWeight: 800, color: '#FFFFFF', fontFamily: '"Outfit", "DM Sans", sans-serif'}}>{weekly_number_of_actions}</Typography>
          <Typography variant="small" sx={{color: 'rgba(255,255,255,0.85)', fontSize: '0.8rem'}}>{translation['dashboardPage'].actionsThisWeek}</Typography>
        </Paper>
        <Box>
          <Typography variant="h3" sx={{fontWeight: 600, mt: '2rem', mb: '.5rem'}}>
            {translation.formatString(translation['dashboardPage'].weeklyActions, weekly_unvalidated || 0)}
          </Typography>
          <Typography sx={{color: '#64748B', fontSize: '0.9rem'}}>{translation['dashboardPage'].awaitingValidation}</Typography>
        </Box>
      </Box>
      <Box sx={{flex: '1', position: 'relative', maxWidth: '282px'}}>
        <Box sx={{position: 'relative', zIndex: '2'}}>
          <Doughnut data={data} aria-label="Doughnut chart of actions" options={options}/>
        </Box>
        <Box sx={{
          position: 'absolute',
          zIndex: '0',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          borderRadius: '99em',
          width: '50%',
          height: '50%',
          display: 'flex',
          alignItems: 'center',
        }}>
          <Typography sx={{textAlign: 'center', padding: '2em', color: '#64748B', fontSize: '0.85rem'}}>{translation['dashboardPage'].actionsBreakdown}</Typography>
        </Box>
      </Box>
    </Box>

    {/* Action Tiles Grid */}
    <Box sx={{
      width: '100%',
      paddingTop: '2em',
      display: 'grid',
      gridTemplateColumns: {md: '1fr 1fr', xsm: '1fr'},
      gridAutoRows: '1fr',
      gap: '1.25em',
    }}>

    {/* Users Action List */}
    {sortedActions.map((action) => (
      <Paper key={action.id} sx={{
        borderRadius: '16px',
        textAlign: 'center',
        flex: '1',
        padding: '1.25em 1.5em 1.5em 1.5em',
        display: 'inline-flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        background: getActionLightBg(action.id),
        border: `1px solid ${getActionColor(action.id)}20`,
        boxShadow: 'none',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 4px 16px ${getActionColor(action.id)}15`,
        },
      }}>
        <Box
          component="img"
          src={action.avatar}
          alt="action avatar"
          sx={{
            maxHeight: '6em',
            maxWidth: '7em',
            margin: '0 auto',
            padding: '0.75em',
          }}
        />
        <Typography sx={{
          fontWeight: 700,
          textTransform: 'uppercase',
          fontSize: '0.85rem',
          letterSpacing: '0.03em',
          color: '#0F172A',
          mb: '.5em',
          fontFamily: '"Outfit", "DM Sans", sans-serif',
        }}>
          {action.action_name}
        </Typography>
        <Typography sx={{lineHeight: '1.6em', paddingBottom: '1em', color: '#475569', fontSize: '0.9rem'}}>
          {action.co2_saved !== 0 && (
            <>
              {translation.co2SavedColon} <strong style={{color: '#0F172A'}}>{action.co2_saved}</strong><br />
            </>
          )}
          {translation['dashboardPage'].actionsTakenColon} <strong style={{color: '#0F172A'}}>{action.number}</strong><br />
          {translation['dashboardPage'].pointsEarnedColon} <strong style={{color: '#0F172A'}}>{action.points_earned}</strong>
        </Typography>
        <Button
          variant="contained"
          component={Link}
          to={getLogActionPath('ENTER_DETAILS', action.id)}
          sx={{
            background: getActionColor(action.id),
            color: '#FFFFFF',
            boxShadow: `0 2px 8px ${getActionColor(action.id)}30`,
            '&:hover': {
              background: getActionColor(action.id),
              opacity: 0.9,
              transform: 'translateY(-1px)',
              boxShadow: `0 4px 12px ${getActionColor(action.id)}40`,
            },
          }}
        >
          {translation['dashboardPage'].logNow}
        </Button>
      </Paper>
    ))}

    {/* empty action tile */}
    {remainingActions.map((action) => (
      <Paper key={action.id} sx={{
        borderRadius: '16px',
        textAlign: 'center',
        flex: '1',
        padding: '1.25em 1.5em 1.5em 1.5em',
        display: 'inline-flex',
        flexDirection: 'column',
        gap: '1em',
        border: `2px dashed ${getActionColor(action.id)}40`,
        background: '#FAFAFA',
        boxShadow: 'none',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: getActionColor(action.id),
          background: getActionLightBg(action.id),
        },
      }}>
        <Box
          component="img"
          src={getActionIcon(action.id)}
          alt=""
          sx={{
            maxHeight: '6em',
            maxWidth: '7em',
            margin: '0 auto',
            padding: '0.75em',
            opacity: 0.7,
          }}
        />
        <Typography sx={{
          fontWeight: 700,
          textTransform: 'uppercase',
          fontSize: '0.85rem',
          letterSpacing: '0.03em',
          color: '#0F172A',
          fontFamily: '"Outfit", "DM Sans", sans-serif',
        }}>
          {action.name}
        </Typography>
        <Typography sx={{
          lineHeight: '1.6em',
          paddingBottom: '1em',
          color: '#64748B',
          fontSize: '0.9rem',
          '& a': {
            color: '#0D9488',
            fontWeight: 600,
          },
        }}>
          {translation['dashboardPage'].emptyAction1}
          <strong>0</strong> {translation['dashboardPage'].emptyAction2}
          <a href={getActionLandingURL(action.id)} target="_blank" rel="noreferrer"
          >{translation['dashboardPage'].emptyAction3}
          </a>?
        </Typography>
        <Button
          variant="outlined"
          component={Link}
          to={getLogActionPath('ENTER_DETAILS', action.id)}
          sx={{
            borderColor: getActionColor(action.id),
            color: getActionColor(action.id),
            '&:hover': {
              borderColor: getActionColor(action.id),
              background: getActionLightBg(action.id),
            },
          }}
        >
          {translation['dashboardPage'].logNow}
        </Button>
      </Paper>
    ))}
    </Box>
    </>
  )
};

export default ActionsTile;
